import { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDarkMode } from "../../components/Theme/useDarkMode";
import { createView, deleteView, errorMessage, listViews, updateView } from "./api";
import { LabelChips } from "./components";
import { ViewFormModal } from "./forms";
import "./ProjectViews.css";

/** Project Views: saved views over Jira epics. */
export default function ProjectViewsPage({ onReady }) {
  const [theme] = useDarkMode();
  const history = useHistory();
  const [views, setViews] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null, "new" or a view

  const load = useCallback(async () => {
    setError("");
    try {
      setViews(await listViews());
    } catch (err) {
      setError(errorMessage(err));
      setViews([]);
    } finally {
      onReady?.();
    }
  }, [onReady]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (input) => {
    try {
      if (editing === "new") {
        const { view } = await createView(input);
        history.push(`/project-views/${view.id}`);
        return;
      }
      await updateView(editing.id, input);
      setEditing(null);
      toast.success("View saved");
      load();
    } catch (err) {
      throw new Error(errorMessage(err));
    }
  };

  const remove = async (view) => {
    if (!window.confirm(`Delete the view "${view.name}"? Its epics stay in Jira.`)) return;
    try {
      await deleteView(view.id);
      toast.success("View deleted");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="pv-page" data-theme={theme}>
      <ToastContainer position="bottom-right" autoClose={3000} />
      <header className="pv-head">
        <div>
          <h1>Project Views</h1>
          <p className="pv-sub">
            Live views over Jira epics. Each project is an epic, picked by its labels; the step it is at is the
            epic's Project Step field.
          </p>
        </div>
        <button type="button" className="pv-btn pv-btn--primary" onClick={() => setEditing("new")}>
          New view
        </button>
      </header>

      {error ? <div className="pv-error">{error}</div> : null}
      {views === null ? <div className="pv-empty">Loading views…</div> : null}
      {views?.length === 0 && !error ? (
        <div className="pv-empty">
          No views yet. Create one with the Jira labels of the epics it should show, for example <code>newfi</code>.
        </div>
      ) : null}

      <div className="pv-cards">
        {(views || []).map((view) => (
          <div key={view.id} className="pv-card">
            <Link to={`/project-views/${view.id}`} className="pv-card-main">
              <h2>{view.name}</h2>
              {view.description ? <p>{view.description}</p> : null}
              <div className="pv-card-meta">
                <LabelChips labels={view.labels} />
                <span>
                  {view.labelMatch === "any" ? "any label" : "every label"} · {view.projectKey}
                </span>
              </div>
              <div className="pv-card-steps">{view.steps.join(" → ")}</div>
            </Link>
            <div className="pv-card-actions">
              <button type="button" className="pv-link-btn" onClick={() => setEditing(view)}>
                Edit
              </button>
              <button type="button" className="pv-link-btn pv-link-btn--danger" onClick={() => remove(view)}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing ? (
        <ViewFormModal view={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSave={save} />
      ) : null}
    </div>
  );
}
